// ============================================
// Search Store (Zustand)
// ============================================
// Manages client-side search index with 5-minute caching

import { create } from 'zustand';
import Fuse, { type IFuseOptions } from 'fuse.js';

/**
 * Entity types included in search index
 */
export type SearchEntityType = 'lead' | 'account' | 'contact' | 'opportunity';

/**
 * Search index item - normalized structure for all entities
 */
export interface SearchIndexItem {
  id: string;
  type: SearchEntityType;
  title: string;
  subtitle: string | null;
  description: string | null;
  status: string | null;
  url: string;
  updatedAt: number;
}

/**
 * Search index response from API
 */
export interface SearchIndexResponse {
  items: SearchIndexItem[];
  generatedAt: number;
  totalCount: number;
}

/**
 * Fuse.js configuration for fuzzy search
 */
const FUSE_OPTIONS: IFuseOptions<SearchIndexItem> = {
  keys: [
    { name: 'title', weight: 0.4 },
    { name: 'subtitle', weight: 0.3 },
    { name: 'description', weight: 0.2 },
    { name: 'type', weight: 0.1 },
  ],
  threshold: 0.4, // Allow for some typos
  ignoreLocation: true,
  includeScore: true,
  minMatchCharLength: 2,
};

/**
 * Cache TTL in milliseconds (5 minutes)
 */
const CACHE_TTL_MS = 5 * 60 * 1000;

interface SearchState {
  // Data
  items: SearchIndexItem[];
  fuse: Fuse<SearchIndexItem> | null;

  // Cache
  lastFetchedAt: number | null;
  isCacheValid: boolean;

  // Loading state
  isLoading: boolean;
  error: string | null;

  // Actions
  setIndex: (items: SearchIndexItem[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  invalidateCache: () => void;
  isCacheStale: () => boolean;

  // Search
  search: (query: string, options?: SearchOptions) => SearchResult[];
}

export interface SearchOptions {
  types?: SearchEntityType[];
  limit?: number;
}

export interface SearchResult {
  item: SearchIndexItem;
  score: number;
}

export const useSearchStore = create<SearchState>()((set, get) => ({
  // Initial state
  items: [],
  fuse: null,
  lastFetchedAt: null,
  isCacheValid: false,
  isLoading: false,
  error: null,

  setIndex: (items: SearchIndexItem[]) => {
    // Create a new Fuse instance with the items
    const fuse = new Fuse(items, FUSE_OPTIONS);

    set({
      items,
      fuse,
      lastFetchedAt: Date.now(),
      isCacheValid: true,
      isLoading: false,
      error: null,
    });
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  setError: (error: string | null) => {
    set({ error, isLoading: false });
  },

  invalidateCache: () => {
    set({ isCacheValid: false });
  },

  isCacheStale: () => {
    const { lastFetchedAt, isCacheValid } = get();

    if (!isCacheValid || !lastFetchedAt) {
      return true;
    }

    const age = Date.now() - lastFetchedAt;
    return age > CACHE_TTL_MS;
  },

  search: (query: string, options?: SearchOptions) => {
    const { fuse, items } = get();
    const limit = options?.limit ?? 10;
    const types = options?.types;

    if (!query.trim()) {
      // Return recent items when no query
      let filtered = items;
      if (types?.length) {
        filtered = items.filter(item => types.includes(item.type));
      }
      return filtered.slice(0, limit).map(item => ({ item, score: 0 }));
    }

    if (!fuse) {
      return [];
    }

    // Perform fuzzy search
    let results = fuse.search(query, { limit: limit * 2 }); // Fetch extra for filtering

    // Filter by types if specified
    if (types?.length) {
      results = results.filter(result => types.includes(result.item.type));
    }

    // Limit results
    return results.slice(0, limit).map(result => ({
      item: result.item,
      score: result.score ?? 0,
    }));
  },
}));

/**
 * Get label for entity type
 */
export function getEntityTypeLabel(type: SearchEntityType): string {
  switch (type) {
    case 'lead':
      return 'Lead';
    case 'account':
      return 'Account';
    case 'contact':
      return 'Contact';
    case 'opportunity':
      return 'Opportunity';
    default:
      return type;
  }
}

/**
 * Get icon name for entity type (for use with Lucide icons)
 */
export function getEntityTypeIcon(type: SearchEntityType): string {
  switch (type) {
    case 'lead':
      return 'UserPlus';
    case 'account':
      return 'Building2';
    case 'contact':
      return 'Users';
    case 'opportunity':
      return 'Target';
    default:
      return 'Circle';
  }
}
